// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import type { PrepNote } from "@/types";
import { formatDisplayDateTime, rawTimeIsMidnight } from "@/lib/date-format";

// Intake progress for one encounter. Drives the intake-status pill + whether the
// per-encounter send/kiosk actions are offered (only when nothing has been sent).
export type IntakeStatus =
  "not_sent" | "link_sent" | "in_progress" | "prep_ready" | "reviewed";

// An encounter as returned on the worklist and (minus patient identity) on the
// patient detail. The encounter is the anchor: reason, intake, prep note hang off it.
export interface PatientAppointment {
  appointmentId: string;
  scheduledAt: string;
  endsAt?: string;
  appointmentType?: string;
  reason?: string;
  providerName?: string;
  clinicName?: string;
  clinicTimezone?: string;
  status?: string;
  interactionId?: string;
  intakeStatus: IntakeStatus;
  // This encounter's own completed prep note (null when none). May diverge from
  // intakeStatus: prep_ready with a null note ⇒ the card shows "prep note pending".
  prepNote?: PrepNote | null;
  evalLink?: {
    token: string;
    expiresAt: string;
    usedAt?: string;
    sentVia?: string;
  } | null;
}

// Intake-status pill tone — same hue system as the prep-status Badge.
export const INTAKE_STATUS_TONE: Record<IntakeStatus, string> = {
  not_sent: "bg-secondary text-muted-foreground",
  link_sent: "bg-blue-500/12 text-blue-700 dark:text-blue-300",
  in_progress: "bg-amber-500/12 text-amber-700 dark:text-amber-300",
  prep_ready: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  reviewed: "bg-muted text-muted-foreground",
};

// Appointment-status pill tone. Unknown statuses fall back to the neutral tone.
const APPT_STATUS_TONE: Record<string, string> = {
  proposed: "bg-secondary text-muted-foreground",
  pending: "bg-secondary text-muted-foreground",
  booked: "bg-blue-500/12 text-blue-700 dark:text-blue-300",
  arrived: "bg-amber-500/12 text-amber-700 dark:text-amber-300",
  checked_in: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
  waitlist: "bg-secondary text-muted-foreground",
  fulfilled: "bg-muted text-muted-foreground",
  cancelled: "bg-red-500/12 text-red-600 dark:text-red-400",
  noshow: "bg-red-500/12 text-red-600 dark:text-red-400",
};

const DEFAULT_STATUS_TONE = "bg-secondary text-muted-foreground";

// Canonical status key (lowercased, hyphen→underscore) for tone + i18n lookup.
export function apptStatusKey(status?: string): string {
  return (status ?? "").toLowerCase().replace(/-/g, "_").trim();
}

export function apptStatusTone(status?: string): string {
  return APPT_STATUS_TONE[apptStatusKey(status)] ?? DEFAULT_STATUS_TONE;
}

export function intakeActionLabelKey(status: IntakeStatus): string {
  if (status === "prep_ready") return "visits.viewPrep";
  if (status === "in_progress") return "visits.viewProgress";
  if (status === "link_sent") return "visits.viewLink";
  return "visits.startIntake";
}

// Statuses at which the patient is physically in the clinic → in-clinic intake.
export function isInClinic(status?: string): boolean {
  const k = apptStatusKey(status);
  return k === "arrived" || k === "checked_in";
}

// Title-case an arbitrary code ("well_child" / "WELL-CHILD" → "Well Child").
export function titleCaseCode(code: string): string {
  return code
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// Canonical visit-type key for i18n, or null when the code has no friendly label
// (caller falls back to title-casing the raw code).
export function visitTypeKey(
  code?: string,
): "followup" | "wellness" | "sick" | "walkin" | null {
  const norm = (code ?? "").toUpperCase().replace(/[\s_-]/g, "");
  if (!norm) return null;
  if (norm === "FOLLOWUP") return "followup";
  if (norm === "WELLNESS") return "wellness";
  if (norm === "SICK") return "sick";
  if (norm === "WALKIN") return "walkin";
  return null;
}

// Wall-clock time + standardized date in the clinic's IANA timezone; falls back to the
// viewer's local zone when the timezone is missing or invalid. Time is hidden for
// date-only or midnight timestamps.
export function formatApptTime(
  iso: string,
  timeZone?: string,
): { time: string; date: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: iso, date: "" };
  const tzOpts = timeZone ? { timeZone } : {};
  const timeOpts = rawTimeIsMidnight(iso)
    ? null
    : { ...tzOpts, hour: "numeric" as const, minute: "2-digit" as const };
  try {
    return {
      time: timeOpts
        ? new Intl.DateTimeFormat(undefined, timeOpts).format(d)
        : "",
      date: formatDisplayDateTime(iso, { timeZone, time: false }),
    };
  } catch {
    return {
      time: rawTimeIsMidnight(iso)
        ? ""
        : new Intl.DateTimeFormat(undefined, {
            hour: "numeric",
            minute: "2-digit",
          }).format(d),
      date: formatDisplayDateTime(iso, { time: false }),
    };
  }
}
